function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CloseIcon } from 'components/Icon';
import { ModalBase } from './ModalBase';
import * as S from './Modal.styles';
export const ModalPrimaryActionButton = /*#__PURE__*/forwardRef(({
  size,
  disabled,
  loading,
  hasPadding,
  outlineFocusColor,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(S.ModalButton, _extends({}, props, {
    variant: "primary",
    size: size === 'small' ? 'medium' : size,
    disabled: disabled,
    loading: loading,
    outlineFocusColor: outlineFocusColor,
    ref: ref
  }));
});
export const ModalSecondaryActionButton = /*#__PURE__*/forwardRef(({
  size: modalSize,
  disabled,
  loading,
  hasPadding,
  outlineFocusColor,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(S.ModalButton, _extends({}, props, {
    variant: "outline",
    size: modalSize === 'small' ? 'medium' : modalSize,
    disabled: disabled,
    loading: loading,
    outlineFocusColor: outlineFocusColor,
    ref: ref
  }));
});
export const ModalTertiaryActionButton = /*#__PURE__*/forwardRef(({
  outlineFocusColor,
  ...props
}, ref) => /*#__PURE__*/React.createElement(S.ModalButton, _extends({}, props, {
  variant: "text",
  hasPadding: false,
  outlineFocusColor: outlineFocusColor,
  ref: ref
})));
export const ModalClose = props => /*#__PURE__*/React.createElement(Dialog.Close, _extends({
  asChild: true
}, props));
export function Modal(props) {
  const {
    children,
    dialogContentProps,
    primaryAction,
    secondaryAction,
    tertiaryAction,
    hideClose,
    portalContainer,
    size = 'medium',
    title,
    trigger,
    hasPadding = true,
    backgroundColor,
    headerBackgroundColor,
    focusOutlineColor,
    ...dialogProps
  } = props;

  /*
   * If the dialog is controlled, we need to wire up the close button
   * to the function that updates the open state
   */
  const handleCloseButtonClick = () => {
    if (dialogProps.onOpenChange) {
      dialogProps.onOpenChange(false);
    }
  };
  const header = title ? /*#__PURE__*/React.createElement(S.ModalHeader, {
    $modalSize: size,
    $backgroundColor: headerBackgroundColor
  }, /*#__PURE__*/React.createElement(S.ModalTitle, {
    $modalSize: size
  }, title), hideClose ? null : /*#__PURE__*/React.createElement(ModalClose, null, /*#__PURE__*/React.createElement(S.ModalHeaderCloseButtonContainer, null, /*#__PURE__*/React.createElement(S.ModalHeaderCloseButton, {
    ariaLabel: "Close",
    onClick: handleCloseButtonClick,
    icon: /*#__PURE__*/React.createElement(CloseIcon, null),
    variant: "text",
    outlineFocusColor: focusOutlineColor
  })))) : null;

  // Clone action buttons to inject focusOutlineColor
  const cloneActionWithFocusColor = action => {
    if (!action || !focusOutlineColor) return action;
    if (/*#__PURE__*/React.isValidElement(action)) {
      return /*#__PURE__*/React.cloneElement(action, {
        outlineFocusColor: focusOutlineColor
      });
    }
    return action;
  };
  const primaryAndSecondaryActionButtons = primaryAction || secondaryAction ? /*#__PURE__*/React.createElement(S.ModalActionsFooterButtonGroup, null, cloneActionWithFocusColor(secondaryAction), cloneActionWithFocusColor(primaryAction)) : null;
  const footer = primaryAction || secondaryAction || tertiaryAction ? /*#__PURE__*/React.createElement(S.ModalActionsFooter, {
    $hasTertiaryAction: !!tertiaryAction
  }, cloneActionWithFocusColor(tertiaryAction), primaryAndSecondaryActionButtons) : null;
  return /*#__PURE__*/React.createElement(ModalBase, _extends({
    backgroundColor: backgroundColor,
    trigger: trigger,
    size: size,
    dialogContentProps: dialogContentProps,
    portalContainer: portalContainer
  }, dialogProps), header, /*#__PURE__*/React.createElement(S.ModalBody, {
    $modalSize: size,
    $hasPadding: hasPadding,
    $backgroundColor: backgroundColor
  }, children), footer);
}